/**
 * Canonical URL helpers.
 *
 * Public URLs are locale-prefixed (`/{locale}/...`), and <link rel="canonical"> in
 * localeLayout.jsx points at exactly that shape. og:url and every JSON-LD `url`/`item`/`@id`
 * must agree with the canonical, otherwise crawlers see two competing addresses for one page.
 * Always build those URLs with absoluteUrl() instead of string-concatenating the origin.
 */

export const SITE_ORIGIN = "https://www.asmaskiner.se";

/** Entity id for the company. Deliberately un-prefixed: it identifies the org, not a page. */
export const ORGANIZATION_ID = SITE_ORIGIN;

export const LOGO_URL = `${SITE_ORIGIN}/images/logo.png`;

/** Registered company number (Bolagsverket). Same value as shown in the footer. */
export const ORGANIZATION_NUMBER = "559447-9486";

export const DEFAULT_COMPANY_NAME = "AS Maskiner Sverige AB";
export const DEFAULT_COMPANY_PHONE = "+4640122700";
export const DEFAULT_COMPANY_EMAIL = "info@asmaskiner.se";

export const SAME_AS = [
  "https://www.facebook.com/asmaskiner",
  "https://www.linkedin.com/company/as-maskiner",
];

export const AREA_SERVED = ["SE", "NO", "FI", "DK"];

/**
 * Absolute, locale-prefixed URL for a public path — matches the canonical link.
 *
 * @param {string} locale e.g. "sv"
 * @param {string} [path] path without the locale prefix, e.g. "/catalogue" or "/"
 * @returns {string} e.g. "https://www.asmaskiner.se/sv/catalogue"
 */
export function absoluteUrl(locale, path = "/") {
  const clean = path.startsWith("/") ? path : `/${path}`;
  return `${SITE_ORIGIN}/${locale}${clean}`;
}

/**
 * A meta description that ends where a sentence ends.
 *
 * The old helper cut on a word boundary and appended "…", which left every long description
 * ending mid-clause in the search result — "Kantlistmaskiner limmar kantlist på skivkanter och
 * putsar, fräser…". Search engines rewrite a snippet like that anyway. Taking whole sentences
 * instead gives a self-contained summary that fits the ~155 characters Google renders.
 *
 * A first sentence that is longer than `max` on its own has nothing to take, so it falls back
 * to the word-boundary cut with an ellipsis — better a clipped sentence than a 300-character
 * one Google truncates for us.
 *
 * @param {string} text
 * @param {number} [max] characters
 * @returns {string} never ends on whitespace
 */
export function metaDescription(text, max = 155) {
  const clean = String(text || "")
    .replace(/\s+/g, " ")
    .trim();
  if (clean.length <= max) return clean;

  // ". " / "! " / "? " and the end of the string. A terminator that is not followed by a space
  // is part of the word (an abbreviation, "3.300 mm", a decimal) and never ends a sentence.
  const boundary = /[.!?]+(?=\s|$)/g;
  let cut = 0;
  for (let match = boundary.exec(clean); match; match = boundary.exec(clean)) {
    const end = match.index + match[0].length;
    if (end > max) break;
    cut = end;
  }
  if (cut > 0) return clean.slice(0, cut);

  // The ellipsis counts towards `max`: with no word boundary before the limit (one long model
  // number or URL) the cut is max - 1 characters, never max.
  const space = clean.lastIndexOf(" ", max - 1);
  return `${clean.slice(0, space > 0 ? space : max - 1).trimEnd()}…`;
}

/**
 * The machine title with its manufacturer in front, unless it is already there.
 *
 * Machine titles are entered by hand and only some of them carry the brand ("KDT KN2609" vs
 * "KN2609DL"), so the <h1>, the <title> and the Product JSON-LD `name` used to disagree about
 * what the product is called. This is the one name all three use.
 *
 * @param {{ title?: string, manufacturer?: string }} machine
 * @returns {string} e.g. "KDT KN2609DL"
 */
export function withBrand(machine) {
  const title = String(machine?.title || "").trim();
  const manufacturer = String(machine?.manufacturer || "").trim();
  if (!manufacturer) return title;
  if (!title) return manufacturer;

  const lower = (value) => value.toLocaleLowerCase("sv-SE");
  return lower(title).startsWith(lower(manufacturer)) ? title : `${manufacturer} ${title}`;
}

export const SITE_NAME = "AS Maskiner";

/**
 * Whether a machine is factory-new.
 *
 * The API localises `condition` on the way out — "Ny"/"Använd" in Swedish, "new"/"used" in
 * English — so comparing to the Swedish word alone marked every machine on an /en/ page as
 * used. Both spellings mean new; anything else is used.
 */
const NEW_CONDITION_VALUES = new Set(["ny", "new"]);

export function isNewMachine(machine) {
  return NEW_CONDITION_VALUES.has(
    String(machine?.condition || "")
      .trim()
      .toLowerCase()
  );
}

/**
 * "<brand> <model> – ny | <kategori> | AS Maskiner".
 *
 * The old template was "AS Maskiner - {title} | {manufacturer} {category}": the site name came
 * first, so every result in the list looked identical, and the category was pasted in whether
 * or not the title already said it. Here the machine comes first, the condition is the thing a
 * buyer filters on, and the category is only added when the name doesn't already contain it.
 *
 * @param {object} machine
 * @returns {string}
 */
export function machineTitle(machine) {
  const name = withBrand(machine);
  const condition = isNewMachine(machine) ? "ny" : "begagnad";
  const category = String(machine?.category || "").trim();

  const lower = (value) => value.toLocaleLowerCase("sv-SE");
  const needsCategory = category && !lower(name).includes(lower(category));

  return [`${name} – ${condition}`, ...(needsCategory ? [category] : []), SITE_NAME].join(" | ");
}

/**
 * The machine's meta description — and og:description, which is the same sentence.
 *
 * The category name is deliberately never pasted into the sentence: the names are plural
 * ("Kantlistmaskiner"), and the old template produced "Ny Kantlistmaskiner från KDT". The
 * category has its own landing page to rank for that word; this one is about the machine.
 *
 * @param {object} machine
 * @returns {string} at most 155 characters, ending on a sentence
 */
export function machineMetaDescription(machine) {
  const name = withBrand(machine);
  const manufacturer = String(machine?.manufacturer || "").trim();
  const year = machine?.buildYear;
  const closing = "Köp eller begär offert hos AS Maskiner, leverantör i Sverige och Norden.";

  const opening = isNewMachine(machine)
    ? `${name} – fabriksny maskin${manufacturer ? ` från ${manufacturer}` : ""}${year ? `, årsmodell ${year}` : ""}.`
    : `${name} – begagnad ${manufacturer ? `${manufacturer}-maskin` : "maskin"}${year ? ` från ${year}` : ""}.`;

  return metaDescription(`${opening} ${closing}`);
}
